/**
 * AdminLogin Component
 * 
 * Product introduction / landing page for AI Work Study.
 * Showcases features and value prop before prompting Microsoft SSO.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/admin.css';

const MicrosoftIcon = () => (
  <svg className="microsoft-icon" viewBox="0 0 21 21" width="21" height="21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

const FEATURES = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
    title: 'Conversational Surveys',
    description: 'Natural chat-based interviews replace boring forms — employees share more, with less fatigue.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
    title: 'JD-Aware Intelligence',
    description: 'AI reads each employee\'s actual job description and tailors every question to their specific role.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
        <line x1="11" y1="8" x2="11" y2="14" />
        <line x1="8" y1="11" x2="14" y2="11" />
      </svg>
    ),
    title: 'Smart Follow-ups',
    description: 'Detects vague answers, automation opportunities, and role drift — then digs deeper automatically.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
    title: 'Instant AI Reports',
    description: 'Comprehensive analysis auto-generated the moment a survey completes — executive-ready insights.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    title: 'Department Trends',
    description: 'Aggregate insights across teams to spot patterns, bottlenecks, and opportunities at scale.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
    title: 'Enterprise Secure',
    description: 'Microsoft SSO, role-based access, and encrypted data — built for enterprise peace of mind.',
  },
];

const STEPS = [
  { number: '1', title: 'Create & Send', description: 'Admins create surveys and send personalized links to employees.' },
  { number: '2', title: 'Chat & Discover', description: 'Employees complete a guided AI conversation about their actual work.' },
  { number: '3', title: 'Analyze & Act', description: 'Leadership gets instant reports with actionable, sourced insights.' },
];

const AdminLogin = () => {
  const { isAuthenticated, loading, error, login } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const featuresRef = useRef(null);
  const howRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated && !loading) {
      window.location.href = '/admin';
    }
  }, [isAuthenticated, loading]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogin = () => login('/admin');

  const scrollTo = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Loading state
  if (loading) {
    return (
      <div className="intro-loading-screen">
        <div className="intro-loading-card">
          <div className="intro-loading-spinner" />
          <p>Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="intro-page">
      {/* Sticky Nav */}
      <nav className={`intro-nav ${scrolled ? 'intro-nav--scrolled' : ''}`}>
        <div className="intro-nav-inner">
          <div className="intro-nav-brand">
            <img src="/logo.jpg" alt="Salt & Straw" className="intro-nav-logo" onError={(e) => { e.target.style.display='none'; }} />
            <span className="intro-nav-title">Work Study</span>
          </div>
          <div className="intro-nav-links">
            <button className="intro-nav-link" onClick={() => scrollTo(featuresRef)}>Features</button>
            <button className="intro-nav-link" onClick={() => scrollTo(howRef)}>How It Works</button>
            <button className="intro-nav-cta" onClick={handleLogin}>
              <MicrosoftIcon /> Sign In
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="intro-hero">
        <div className="intro-hero-bg" />
        <div className="intro-hero-content">
          <div className="intro-hero-badge">AI-Powered Workforce Intelligence</div>
          <h1 className="intro-hero-headline">
            Understand how your<br />team <em>actually</em> works
          </h1>
          <p className="intro-hero-sub">
            AI Work Study replaces static surveys with intelligent conversations that uncover role drift, hidden bottlenecks, and automation opportunities — then delivers executive-ready reports in seconds.
          </p>

          {error && (
            <div className="intro-hero-error">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <div className="intro-hero-actions">
            <button className="intro-btn-primary" onClick={handleLogin}>
              <MicrosoftIcon /> Sign in with Microsoft
            </button>
            <button className="intro-btn-secondary" onClick={() => scrollTo(featuresRef)}>
              Learn More ↓
            </button>
          </div>

          <p className="intro-hero-note">Secure access for authorized Salt &amp; Straw employees</p>
        </div>
      </section>

      {/* Features */}
      <section className="intro-features" ref={featuresRef}>
        <div className="intro-section-inner">
          <h2 className="intro-section-heading">Everything you need to understand your workforce</h2>
          <p className="intro-section-sub">Purpose-built for People Operations — from survey creation to leadership insights.</p>
          <div className="intro-features-grid">
            {FEATURES.map((f, i) => (
              <div className="intro-feature-card" key={i}>
                <div className="intro-feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="intro-how" ref={howRef}>
        <div className="intro-section-inner">
          <h2 className="intro-section-heading">How It Works</h2>
          <p className="intro-section-sub">Three steps from survey to strategic insight.</p>
          <div className="intro-steps">
            {STEPS.map((s) => (
              <div className="intro-step" key={s.number}>
                <div className="intro-step-number">{s.number}</div>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="intro-cta-section">
        <div className="intro-section-inner intro-cta-inner">
          <h2>Ready to see the full picture?</h2>
          <p>Sign in with your Salt &amp; Straw Microsoft account to access the People Ops dashboard.</p>
          <button className="intro-btn-primary intro-btn-lg" onClick={handleLogin}>
            <MicrosoftIcon /> Sign in with Microsoft
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="intro-footer">
        <p>© {new Date().getFullYear()} Salt &amp; Straw — AI Work Study · Built for People Operations</p>
      </footer>
    </div>
  );
};

export default AdminLogin;
